<template>
  <div class="w-full">
    <Teleport v-if="mounted" to="#header-breadcrumbs">
      <SBreadcrumb v-bind="{ routes }" v-if="isDesktop || !binanceBalance" />
      <BinanceBalance v-if="isMobile && binanceBalance" />
    </Teleport>
    <CVerification />
    <main class="grid gap-2" v-if="mounted">
      <ProfitStatistics
        :title="$t('total')"
        :total-profit="dashboardData?.total_profit"
        :percentage="dashboardData?.total_profit_percentage"
        :closed-trades="dashboardData?.closed_trades_total"
        :closed-trades-percentage="dashboardData?.closed_trades_percentage"
        :assigned-money="dashboardData?.total_assigned_money"
        :last_week="dashboardData?.total_profit_week"
        :last_month="dashboardData?.total_profit_month"
        :last_week_percentage="dashboardData?.total_profit_week_percentage"
        :last_month_percentage="dashboardData?.total_profit_month_percentage"
        :loading="loading"
      />
      <div class="flex items-stretch lg:flex-row flex-col gap-2">
        <CHomeLineChart
          :title="$t('current_profit_loss')"
          :total-profit="dashboardData?.total_profit"
          :percentage="dashboardData?.total_profit_percentage"
          :closed-trades="dashboardData?.closed_trades_total"
          :closed-trades-percentage="dashboardData?.closed_trades_percentage"
          :assigned-money="dashboardData?.total_assigned_money"
        />
        <div
          v-if="false"
          class="lg:max-w-[340px] w-full flex items-stretch flex-col gap-2"
        >
          <HistoryInfo
            @updateIds="handleUpdateIds"
            v-bind="{ months, historyData }"
          />
          <CWinLosChart
            class="w-full h-full"
            :win="dashboardData?.total_wins ?? 0"
            :lost="dashboardData?.total_losses ?? 0"
          />
        </div>
      </div>
      <div class="flex items-start lg:flex-row flex-col gap-2">
        <CPerformanceTable class="w-full" />
        <div
          :class="{
            'lg:max-w-[340px] w-full':
              (dashboardData?.strategy_profits?.length &&
                dashboardData?.total_profit) ||
              (dashboardData?.total_wins && dashboardData?.total_losses),
          }"
          class="flex-col space-y-2"
        >
          <CWinLosChart
            class="w-full h-fit"
            :win="dashboardData?.total_wins ?? 0"
            :lost="dashboardData?.total_losses ?? 0"
          />
          <div
            v-if="
              (dashboardData?.strategy_profits?.length &&
                dashboardData?.total_profit) ||
              (dashboardData?.total_wins && dashboardData?.total_losses)
            "
            class="flex items-start flex-col h-auto gap-2"
          >
            <CProfitChart
              class="w-full"
              :strategies="dashboardData?.strategy_profits"
              :loading="loading"
            />
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useMounted } from "@/composables/useMounted";
import SBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import { useI18n } from "vue-i18n";
import { computed, onMounted } from "vue";
import CHomeLineChart from "@/modules/Dashboard/components/CHomeLineChart.vue";
import CVerification from "@/components/Common/CVerification.vue";
import CPerformanceTable from "@/modules/Dashboard/components/CPerformanceTable.vue";
import CProfitChart from "@/modules/Dashboard/components/CProfitChart.vue";
import { useDashboardStore } from "@/modules/Dashboard/store";
import CWinLosChart from "@/modules/Dashboard/components/CWinLosChart.vue";
import ProfitStatistics from "@/modules/Dashboard/components/ProfitStatistics.vue";
import { useMobile } from "@/composables/useMobile";
import BinanceBalance from "@/components/Common/BinanceBalance.vue";
import { useAuthStore } from "@/modules/Auth/stores";
import HistoryInfo from "@/modules/Dashboard/components/HistoryInfo.vue";

import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
const { pushToDataLayer } = usePushToDataLayer();
const { mounted } = useMounted();
const { t } = useI18n();
const dashboardStore = useDashboardStore();
const store = useAuthStore();

const { isMobile } = useMobile("mobile");
const { isDesktop } = useMobile("desktop");

const dashboardData = computed(() => dashboardStore.data);
const loading = computed(() => dashboardStore.loading);
const historyData = computed(() => {
  return [
    {
      title: "dashboard_info.all_closed_trades",
      value: dashboardStore.historyData?.total_profit_closed_coin,
      percentage: dashboardStore.historyData?.profit_closed_percentage,
    },
  ];
});
const binanceBalance = computed(
  () => store.user.unique_exchanges?.[0]?.usdt_balance,
);
const routes = computed(() => [
  {
    name: t("dashboard_title"),
    route: "/",
  },
]);
const months = [
  {
    id: "all",
    label: "all",
  },
  {
    id: 1,
    label: "january",
  },
  {
    id: 2,
    label: "february",
  },
  {
    id: 3,
    label: "march",
  },
  {
    id: 4,
    label: "april",
  },
  {
    id: 5,
    label: "may",
  },
  {
    id: 6,
    label: "june",
  },
  {
    id: 7,
    label: "july",
  },
  {
    id: 8,
    label: "august",
  },
  {
    id: 9,
    label: "september",
  },
  {
    id: 10,
    label: "october",
  },
  {
    id: 11,
    label: "november",
  },
  {
    id: 12,
    label: "december",
  },
] as const;

const handleUpdateIds = (data) => {
  if (data?.length === 1 && data?.find((el) => el === "all")) {
    dashboardStore.fetchHistoryData(
      months?.filter((el) => el?.id !== "all").map((el) => el?.id),
    );
  } else {
    if (data?.length !== 1 && data?.includes("all")) {
      dashboardStore.fetchHistoryData(
        months?.filter((el) => el?.id !== "all").map((el) => el?.id),
      );
    } else {
      dashboardStore.fetchHistoryData(data?.filter((el) => el?.id !== "all"));
    }
  }
};

dashboardStore.fetchMainData();
dashboardStore.fetchHistoryData(
  months?.filter((el) => el?.id !== "all").map((el) => el?.id),
);
onMounted(() => {
  pushToDataLayer("pageview", {
    page: "dashboard",
    event_category: "page",
    event_label: "dashboard_page_view",
    button_text: "None",
  });
});
</script>
